import {
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  inject,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';

import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorToastInterceptor } from './core/interceptors/error-toast.interceptor';
import { isAccessTokenExpired } from './core/jwt.util';
import { AuthService } from './core/services/auth.service';
import { SessionService } from './core/services/session.service';
import { CommunicationSocketService } from './core/services/communication-socket.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withViewTransitions(),
    ),
    provideHttpClient(withInterceptors([authInterceptor, errorToastInterceptor])),
    // Restore session: refresh an expired access token, then reconnect chat.
    provideAppInitializer(async () => {
      const session = inject(SessionService);
      const auth = inject(AuthService);
      const socket = inject(CommunicationSocketService);

      if (!session.isAuthenticated()) {
        return;
      }

      const token = session.token();
      if (!token) {
        return;
      }

      if (isAccessTokenExpired(token) && session.refreshToken()) {
        try {
          const refreshed = await firstValueFrom(auth.refreshSession());
          socket.connect(refreshed.token);
        } catch {
          auth.logout();
        }
        return;
      }

      socket.connect(token);
    }),
  ],
};
